import { useState, useEffect, useRef } from 'react';
import { Search, Send, Trash2, Plus, MessageSquare, Loader2, Copy, Check, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import api from '../lib/api';
import ReactMarkdown from 'react-markdown';

export default function GrantResearcher() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Generate or load session ID
  useEffect(() => {
    const storedSessionId = localStorage.getItem('grantResearcherSessionId');
    if (storedSessionId) {
      setSessionId(storedSessionId);
      loadChatHistory(storedSessionId);
    } else {
      const newSessionId = crypto.randomUUID();
      setSessionId(newSessionId);
      localStorage.setItem('grantResearcherSessionId', newSessionId);
    }
    loadSessions();
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = async () => {
    try {
      const response = await api.get('/agents/sessions');
      const grantSessions = response.data.sessions.filter(s => s.agent_type === 'grant_researcher');
      setSessions(grantSessions);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const loadChatHistory = async (sid) => {
    try {
      const response = await api.get(`/agents/grant-researcher/history/${sid}`);
      if (response.data.messages && response.data.messages.length > 0) {
        setMessages(response.data.messages.map(m => ({
          role: m.role,
          content: m.content,
          timestamp: m.timestamp
        })));
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await api.post('/agents/grant-researcher/chat', {
        message: userMessage.content,
        session_id: sessionId
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      loadSessions();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
      // Remove the user message if failed
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const startNewSession = () => {
    const newSessionId = crypto.randomUUID();
    setSessionId(newSessionId);
    setMessages([]);
    localStorage.setItem('grantResearcherSessionId', newSessionId);
    toast.success('Started new conversation');
  };

  const switchSession = (sid) => {
    setSessionId(sid);
    localStorage.setItem('grantResearcherSessionId', sid);
    loadChatHistory(sid);
  };

  const clearSession = async () => {
    if (!sessionId) return;
    
    try {
      await api.delete(`/agents/grant-researcher/session/${sessionId}`);
      setMessages([]);
      startNewSession();
      loadSessions();
      toast.success('Conversation cleared');
    } catch (error) {
      console.error('Error clearing session:', error);
      toast.error('Failed to clear conversation');
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
    toast.success('Copied to clipboard');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedPrompts = [
    "Find federal grants for small businesses in renewable energy",
    "What are the best foundation grants for education nonprofits?",
    "Help me find grants for healthcare research with deadlines in the next 3 months",
    "What grants are available for minority-owned technology startups?"
  ];

  return (
    <div className="space-y-6 h-[calc(100vh-120px)] flex flex-col" data-testid="grant-researcher-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-theme-primary/20 flex items-center justify-center">
            <Search className="w-7 h-7 text-theme-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-theme-light font-mono">Grant Researcher</h1>
            <p className="text-theme-muted text-sm">Find and analyze funding opportunities</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={startNewSession}
            className="border-theme-primary text-theme-primary hover:bg-theme-primary/10"
            data-testid="new-chat-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Chat
          </Button>
          <Button
            variant="outline"
            onClick={clearSession}
            className="border-theme-danger text-theme-danger hover:bg-theme-danger/10"
            data-testid="clear-chat-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear
          </Button>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Sessions Sidebar */}
        <div className="w-64 bg-theme-darker rounded-xl border border-theme-primary/20 p-4 hidden lg:block">
          <h3 className="text-theme-secondary font-mono text-sm mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Recent Sessions
          </h3>
          <ScrollArea className="h-[calc(100%-2rem)]">
            <div className="space-y-2">
              {sessions.length === 0 ? (
                <p className="text-theme-muted text-xs">No previous sessions</p>
              ) : (
                sessions.map((session, idx) => (
                  <button
                    key={idx}
                    onClick={() => switchSession(session.session_id)}
                    className={`w-full text-left p-2 rounded-lg text-xs transition-colors ${
                      sessionId === session.session_id
                        ? 'bg-theme-primary/20 text-theme-light'
                        : 'hover:bg-theme-dark text-theme-muted'
                    }`}
                  >
                    <div className="truncate">{session.last_message || 'New conversation'}</div>
                    <div className="text-theme-muted text-[10px] mt-1">
                      {session.message_count} messages
                    </div>
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-theme-darker rounded-xl border border-theme-primary/20">
          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-8">
                <div className="w-20 h-20 rounded-full bg-theme-primary/10 flex items-center justify-center mb-4">
                  <BookOpen className="w-10 h-10 text-theme-primary" />
                </div>
                <h2 className="text-xl font-bold text-theme-light mb-2">Grant Research Assistant</h2>
                <p className="text-theme-muted max-w-md mb-6">
                  I can help you find government grants, foundation funding, and research opportunities. 
                  Ask me about grants for your specific needs!
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-2xl">
                  {suggestedPrompts.map((prompt, idx) => (
                    <button
                      key={idx}
                      onClick={() => setInputMessage(prompt)}
                      className="text-left p-3 rounded-lg bg-theme-dark hover:bg-theme-primary/10 text-theme-muted hover:text-theme-light text-sm transition-colors border border-theme-primary/10"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message, idx) => (
                  <div
                    key={idx}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-xl p-4 ${
                        message.role === 'user'
                          ? 'bg-theme-primary text-white'
                          : 'bg-theme-dark text-theme-light'
                      }`}
                    >
                      {message.role === 'assistant' ? (
                        <div className="prose prose-invert prose-sm max-w-none">
                          <ReactMarkdown>{message.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <p>{message.content}</p>
                      )}
                      {message.role === 'assistant' && (
                        <button
                          onClick={() => copyToClipboard(message.content, idx)}
                          className="mt-2 text-theme-muted hover:text-theme-light transition-colors"
                        >
                          {copiedIndex === idx ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-theme-dark rounded-xl p-4 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-theme-primary" />
                      <span className="text-theme-muted">Researching grants...</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input Area */}
          <div className="p-4 border-t border-theme-primary/20">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about grants, funding opportunities, eligibility..."
                className="flex-1 bg-theme-dark border-theme-primary/30 text-theme-light placeholder:text-theme-muted"
                disabled={isLoading}
                data-testid="chat-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="bg-theme-primary hover:bg-theme-primary/80 text-white"
                data-testid="send-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
